import React, { useEffect } from 'react';
import styles from './variantPanel.scss';
import { inject, observer } from 'mobx-react';
import { IVariantStore } from '../../types';
import classNames from 'classnames';
import BrickItemsPanel from './brickItemsPanel/brickItemsPanel';
import TabNavigationHelper from '../../helpers/tabNavigationHelper';
import Cross from '../icons/cross';
import Apply from '../icons/apply';
import VariantSelection from './variantSelection/variantSelection';
import ErrorBoundary from '../../helpers/errorBoundary';

const VariantPanel = inject('variantStore')(
    observer(({ variantStore }: IVariantStore) => {
        const tabNavigation: TabNavigationHelper = TabNavigationHelper.instance;
        const variantPanelRef = React.useRef<HTMLDivElement>(null);

        function handleClickOutside(event) {
            // prevent closing the panel if the details modal is open:
            if (variantStore?.detailsModalOpen) {
                event.stopPropagation();
                return;
            }

            // if target is brick in order modal, keep vp and vs open:
            // if the target is btn to open vs, keep vp and vs open:
            // if the target is variant_button, keep vp and vs open
            if (variantStore?.isVariantPanelOpen && variantStore.allowedInteraction) {
                const variantButtonList = Array.from(document.getElementsByClassName('fsp-variant_button'));
                const variantButtonClicked = variantButtonList.find(element => {
                    return element.contains(event.target);
                });

                const omBrickListItemList = Array.from(document.getElementsByClassName('fsp-brick_list_item'));
                const omBrickButtonClicked = omBrickListItemList.find(element => {
                    return element.contains(event.target);
                });

                if (variantPanelRef.current && !variantStore.mainStore.isMobileShop
                    && !variantButtonClicked
                    && !omBrickButtonClicked) {
                    if (!variantPanelRef.current.contains(event.target)) {
                        variantStore.changeVariantPanelDisplay(false);
                        event.stopPropagation();
                    }
                }
            }
        }

        const applyChange = () => {
            if (variantStore?.isVariantPanelOpen)
                variantStore.changeVariantPanelDisplay(false);
        };

        const revertSelectionAndClosePanel = () => {
            // this.props.variantStore?.resetVariantState();
            if (variantStore?.isVariantPanelOpen)
                variantStore.changeVariantPanelDisplay(false);
        };

        useEffect(() => {
            if (!variantStore?.isDeskTopView())
                window.addEventListener(
                    'orientationchange',
                    revertSelectionAndClosePanel
                );

            document.addEventListener('mousedown', handleClickOutside);
            variantStore?.mainStore.setStickyHeaderTop();

            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                document.removeEventListener('orientationchange', revertSelectionAndClosePanel);
            };
        }, []);

        useEffect(() => {
            if (variantPanelRef.current) {
                tabNavigation.setVariantPanelAttributes(variantPanelRef.current, variantStore?.isVariantPanelOpen)
                tabNavigation.toggleDisabledAtt(variantPanelRef.current, !variantStore?.isVariantPanelOpen);
                if (variantStore?.isVariantPanelOpen === true) {
                    const firstFocusable = variantPanelRef.current.querySelectorAll('[focusable]')[0];
                    window.shell.tabNav.focus(firstFocusable);
                }
            }
        }, [variantStore?.isVariantPanelOpen]);

        const brickItems = variantStore?.getSortedBrickItems || [];

        const skipItemSelectionView = brickItems.length === 1 &&
            (
                brickItems[0].variantSelection.showColorSwitcher
                || brickItems[0].variantSelection.showModelSwitcher
                || brickItems[0].variantSelection.showSizeSwitcher
            )

        return (
            <div data-testid='variant_panel' data-semantic-id='variant_panel'
                className={classNames(styles.variant_panel,
                    {
                        [styles.show]: variantStore?.isVariantPanelOpen,
                        [styles.extended_view]: variantStore?.isVariantSelectionPanelOpen,
                        [styles.not_single_article]: !skipItemSelectionView

                    })}
                ref={variantPanelRef}>
                <div className={styles.header}>
                    {(variantStore?.getSortedBrickItems.length ?? 0) > 1 && <span className={styles.vp_title}>{
                        variantStore?.mainStore.localization.generalL10n.selectYourArticle}
                    </span>}
                    {!variantStore?.isVariantSelectionPanelOpen && <div className={styles.close_cross_btn}
                        onClick={revertSelectionAndClosePanel}
                        ref={(el) => tabNavigation.setFocusAtt(el)}><Cross />
                    </div>}

                </div>
                <div className={styles.content}>
                    <ErrorBoundary fallback={<p>Something went wrong BrickItemsPanel</p>}>
                        <BrickItemsPanel />
                    </ErrorBoundary>
                    {variantStore?.sbiVariantSelection &&
                        variantStore?.isVariantPanelOpen && <VariantSelection />}
                </div>
                <div className={classNames(styles.footer,
                    {
                        [styles.is_single_article]: variantStore?.getSortedBrickItems.length === 1,
                        [styles.hide_me]: variantStore?.isVariantSelectionPanelOpen
                    }
                )}>
                    <div
                        className={classNames(styles.button, styles.cancel, 'variant_panel_close_button')}
                        data-testid='footer-cancel-button' data-semantic-id='footer-cancel-button'
                        onClick={revertSelectionAndClosePanel}
                        ref={(el) => tabNavigation.setFocusAtt(el)}
                        onKeyDown={tabNavigation.handleKeyDown}
                    >
                        <Cross />
                        <div className={styles.button_title}>
                            {variantStore?.mainStore.localization.generalL10n.buttonClose}
                        </div>
                    </div>
                    <div
                        className={classNames(styles.button, styles.apply, 'variant_panel_apply_button')}
                        onClick={applyChange}
                        data-testid='flexset-button-layer_1-confirm' data-semantic-id='flexset-button-layer_1-confirm'
                        data-flexset-id={variantStore?.selectedBrickItem.fsCode}
                        data-flexset-name={variantStore?.mainStore.dataStore.orderModule.flexSetInfo.displayName}
                        data-flexset-module-layer_0-category={variantStore?.getOMBrickSelection?.mainNavigationKeyPath}
                        data-flexset-module-layer_0-index={variantStore?.getOMBrickPosition}
                        data-flexset-module-layer_1-category={variantStore?.selectedBrickItem.mainNavigationKeyPath}
                        data-flexset-module-layer_1-index={variantStore?.brickItemPosition}
                        data-flexset-item-name={variantStore?.selectedBrickItem.salesDesignation}
                        data-flexset-item-sku={variantStore?.mainStore.
                          getSalesArticleNo(variantStore?.selectedBrickItem.selectedSavKey)}
                        ref={(el) => tabNavigation.setFocusAtt(el)}
                    >
                        <Apply />
                        <div className={styles.button_title}>
                            {variantStore?.mainStore.localization.generalL10n.buttonApply}
                        </div>
                    </div>
                </div>
            </div>
        );
    }));

export default VariantPanel;

